import type { DataModelAttribute, Item } from '@/types/DataModel';

export const PUBLICATION_MODE = {
    PUBLISH: 'PUBLISH',
    DEPUBLISH: 'DEPUBLISH',
} as const;

export type PublicationMode = typeof PUBLICATION_MODE[keyof typeof PUBLICATION_MODE];

export type DestinationType = 'IN_PLATFORM' | 'COMMUNICATION_CHANNEL';

export type PublicationDestination = {
    destinationId: number | string;
    destinationType: DestinationType;
    subDestination?: string;
};

export type OrganizationDestination = {
    organizationId: number;
    name?: string;
    address?: string;
    email?: string;
    url?: string;
    imageUrl?: string;
    state?: string;
    contactRole?: string | null;
    itemNamespaceOrganizationId?: number;
    destinationType?: DestinationType;
    isSelected?: boolean;
};

export type ChannelDestination = {
    id: number;
    name?: string;
    active?: boolean;
    direction?: string;
    type?: string;
    plan?: string;
    format?: string;
    planLabel?: string;
    subDestinations?: Record<string, string>;
    subDestinationKey?: string;
    subDestinationLabel?: string;
    destinationType?: DestinationType;
    isSelected?: boolean;
};

export type CommunicationPlan = {
    name?: string;
    label?: string;
    supportsSubDestinations?: boolean;
    planActions?: {
        publish_action?: string[];
        unpublish_action?: string[];
    };
    publicationAttributes?: DataModelAttribute[];
    unpublicationAttributes?: DataModelAttribute[];
    publicationCategory?: string;
    unpublicationCategory?: string;
};

export type CommunicationPlans = Record<string, CommunicationPlan>;

export type JobSchedule = {
    active?: boolean;
    startDateTime?: Date | string | number | null;
    endDateTime?: Date | string | number | null;
    nextRunDateTime?: Date | string | number | null;
    repeatType?: string;
    repeatEvery?: number;
    repeatDaysOfWeek?: number[];
    repeatDaysOfMonth?: number[];
    [key: string]: unknown;
};

export type PublicationJob = {
    id?: number;
    type?: string;
    name?: string;
    publicationType?: string;
    destinationChannelIds?: Array<number | string>;
    destinationOrganizationIds?: Array<number | string>;
    channelSubDestinations?: string[];
    publicationValues?: Record<string, unknown> | null;
    publicationValuesPerDestinationChannel?: Record<string, Record<string, unknown>>;
    selectionId?: string;
    schedule?: JobSchedule;
};

export type ItemSelection = {
    primaryKeys?: string[];
    itemsQuery?: Record<string, unknown> & {
        primaryKeys?: string[];
        selectionId?: string;
    };
    selectionId?: string;
};

export type PublicationData = {
    job?: PublicationJob;
    selectionId?: string;
};

export type PlanAction = 'IMMEDIATELY' | 'SCHEDULE_PLAN';

export type PlanOptions = {
    action: PlanAction;
    actionRepeat?: boolean;
    job: PublicationJob;
};

export type PublicationTarget =
    | OrganizationDestination
    | ChannelDestination
    | PublicationDestination;

export type CustomPublicationSettings = {
    filterPublicationTargets?: (
        targets: PublicationTarget[],
        mode: PublicationMode,
        user: unknown,
        organization: unknown,
    ) => PublicationTarget[];
    enablePublicationSchedule?: (
        primaryKeys: string[] | undefined,
        destinations: PublicationDestination[],
        user: unknown,
        organization: unknown,
    ) => boolean;
    selectPublicationTargets?: (
        primaryKeys: string[] | undefined,
        destinations: PublicationTarget[],
        user: unknown,
        organization: unknown,
    ) => unknown;
    allowInvalidItem?: () => boolean;
};

export type ItemsByChannel = Record<string, Item>;

export type PublicationValuesPayload = {
    publicationValues: Record<string, unknown> | null | undefined;
    publicationValuesPerDestinationChannel: Record<string, Record<string, unknown>> | undefined;
};

export type ChannelTypeVerification = Record<string, boolean>;
